import {
  Box,
  Collapse,
  Divider,
  Flex,
  Icon,
  Tag,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import {
  ExternalIngestionDocumentFragment,
  ExternalValidationReportDataFragment,
  IngestionDocumentReviewFragment,
  IngestionDocumentReviewStatus,
  IngestionDocumentValidationErrorReviewFragment,
} from '@client/graphql/__generated__/types';
import {
  CheckIcon,
  ChevronDownIcon,
  FileTextIcon,
  MinusIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import { formatComplianceStatus } from '../utils/ingestionDocuments.utils';
import {
  getContractDocDisplayName,
  getErrorAndSuccessCountsForIngestionDocument,
  hasDocumentBeenValidated,
} from '../utils/validation.utils';

enum ValidationStatus {
  ERROR = 'error',
  SUCCESS = 'success',
  PENDING = 'pending',
  APPROVED = 'approved',
  RETURNED = 'returned',
  NONE = 'none',
  UNCLASSIFIED = 'unclassified',
}

export interface DocumentItem {
  document: ExternalIngestionDocumentFragment;
  contractDocumentId?: string;
}

interface ComplianceDocumentSidebarItemProps {
  documentItem: DocumentItem;
  validationData: ExternalValidationReportDataFragment | null;
  documentReviews?: IngestionDocumentReviewFragment[];
  submittedDocumentReviews?: IngestionDocumentReviewFragment[];
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[];
  isSelected: boolean;
  shouldShowStatus: boolean;
  onSelect: (
    document: ExternalIngestionDocumentFragment,
    contractDocumentId?: string
  ) => void;
  showSubtitle?: boolean;
  subtitleType?: 'folder' | 'status';
}

function getValidationStatus(
  isValidated: boolean,
  errorCount: number,
  shouldShowStatus: boolean,
  documentReview?: IngestionDocumentReviewFragment | null,
  isClassified?: boolean
): ValidationStatus {
  if (!shouldShowStatus) {
    return ValidationStatus.NONE;
  }

  // If document has a review, show the review status instead of validation status
  if (documentReview) {
    return documentReview.status === IngestionDocumentReviewStatus.APPROVED
      ? ValidationStatus.APPROVED
      : ValidationStatus.RETURNED;
  }

  if (!isValidated) {
    if (!isClassified) {
      return ValidationStatus.UNCLASSIFIED;
    }

    return ValidationStatus.PENDING;
  }

  return errorCount > 0 ? ValidationStatus.ERROR : ValidationStatus.SUCCESS;
}

function ValidationStatusIcon({
  status,
  errorCount,
}: {
  status: ValidationStatus;
  errorCount: number;
}) {
  if (status === ValidationStatus.NONE) {
    return null;
  }

  const commonTagProps = {
    borderRadius: 'xs' as const,
    flexShrink: 0,
    size: 'sm' as const,
  };

  switch (status) {
    case ValidationStatus.ERROR:
      return (
        <Tag {...commonTagProps}>
          <Flex align="center" gap={1}>
            <Icon
              as={TriangleAlertIcon}
              color="compliance.icon.error"
              size={12}
            />
            <Text fontSize="xs">{errorCount}</Text>
          </Flex>
        </Tag>
      );

    case ValidationStatus.SUCCESS:
      return (
        <Tag {...commonTagProps}>
          <Flex align="center" gap={1}>
            <Icon as={CheckIcon} color="compliance.icon.success" size={12} />
          </Flex>
        </Tag>
      );

    case ValidationStatus.PENDING:
      return (
        <Tag {...commonTagProps}>
          <Flex align="center" gap={1}>
            <Icon as={MinusIcon} color="compliance.text.muted" size={12} />
          </Flex>
        </Tag>
      );

    case ValidationStatus.UNCLASSIFIED:
      return (
        <Tag {...commonTagProps}>
          <Flex align="center" gap={1}>
            <Icon as={MinusIcon} color="compliance.text.muted" size={12} />
          </Flex>
        </Tag>
      );

    case ValidationStatus.APPROVED:
      return (
        <Tag {...commonTagProps}>
          <Text color="compliance.status.success" fontSize="xs">
            Approved
          </Text>
        </Tag>
      );

    case ValidationStatus.RETURNED:
      return (
        <Tag {...commonTagProps}>
          <Text color="compliance.text.primary" fontSize="xs">
            Returned
          </Text>
        </Tag>
      );

    default:
      return null;
  }
}

export const ComplianceDocumentSidebarItem: FC<
  ComplianceDocumentSidebarItemProps
> = ({
  documentItem,
  validationData,
  documentReviews,
  submittedDocumentReviews,
  hiddenDocumentValidationReviews,
  isSelected,
  shouldShowStatus,
  onSelect,
  showSubtitle = false,
  subtitleType,
}) => {
  const { document } = documentItem;
  const contractDocuments = document.contractDocuments || [];
  const hasMultipleContractDocs = contractDocuments.length > 1;
  const [isCollapsed, setIsCollapsed] = useState(!hasMultipleContractDocs);

  // Find the matching document review (prioritize unsubmitted over submitted)
  const documentReview = documentReviews?.find(
    (review) => review.ingestionDocument.id === document.id
  );

  // If no unsubmitted review, look for submitted review as fallback
  const submittedReview = submittedDocumentReviews?.find(
    (review) => review.ingestionDocument.id === document.id
  );

  // Use unsubmitted review if available, otherwise use submitted review
  const reviewToDisplay = documentReview || submittedReview;

  // Auto-collapse when document becomes unselected, auto-expand when selected
  useEffect(() => {
    if (hasMultipleContractDocs) {
      setIsCollapsed(!isSelected);
    }
  }, [isSelected, hasMultipleContractDocs]);

  const isValidated = hasDocumentBeenValidated(document, validationData);
  const { errors: totalErrorCount } = isValidated
    ? getErrorAndSuccessCountsForIngestionDocument(
        document,
        validationData,
        undefined,
        hiddenDocumentValidationReviews
      )
    : { errors: 0 };

  const validationStatus = getValidationStatus(
    isValidated,
    totalErrorCount,
    shouldShowStatus,
    reviewToDisplay,
    document.isClassified
  );

  const handleMainClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasMultipleContractDocs) {
      // For multiple contract docs, just select the document
      onSelect(document);
    } else {
      // For single contract doc, select with contract doc ID
      const contractDocId = contractDocuments[0]?.id;
      onSelect(document, contractDocId);
    }
  };

  const handleContractDocClick = (
    contractDocId: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    onSelect(document, contractDocId);
  };

  const toggleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasMultipleContractDocs) {
      setIsCollapsed(!isCollapsed);
    }
  };

  const getContractDocErrorCount = (contractDocId: string) => {
    return isValidated
      ? getErrorAndSuccessCountsForIngestionDocument(
          document,
          validationData,
          contractDocId,
          hiddenDocumentValidationReviews
        ).errors
      : 0;
  };

  // Always show ingestion document name as header
  return (
    <Box
      bg={isSelected ? 'compliance.selected.bg' : 'compliance.card.bg'}
      border="1px solid"
      borderColor={
        isSelected ? 'compliance.selected.border' : 'compliance.card.border'
      }
      borderRadius="md"
      p={3}
    >
      <VStack align="stretch" spacing={0.5}>
        <Flex
          _hover={{
            color: isSelected
              ? 'compliance.text.primary'
              : 'compliance.text.secondary',
          }}
          align="center"
          color={
            isSelected ? 'compliance.text.primary' : 'compliance.text.muted'
          }
          cursor="pointer"
          gap={1}
          justifyContent="space-between"
          onClick={handleMainClick}
        >
          <Flex align="center" gap={1} overflow="hidden">
            {hasMultipleContractDocs && (
              <Icon
                as={ChevronDownIcon}
                h={3}
                transform={isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)'}
                transition="transform 0.2s"
                w={3}
                onClick={toggleCollapse}
              />
            )}
            <Flex position="relative" py={0.5}>
              <Icon
                as={FileTextIcon}
                color="compliance.text.muted"
                h={3.5}
                w={3.5}
              />
              {subtitleType === 'folder' && reviewToDisplay && (
                <Box
                  bg="cyan.500"
                  borderRadius="full"
                  h={1.5}
                  position="absolute"
                  right={0}
                  top={0}
                  w={1.5}
                />
              )}
            </Flex>

            {/* Contract doc form tag for non-multiple contract docs */}
            {!hasMultipleContractDocs &&
              (contractDocuments[0]?.documentVersion?.document?.form ||
                contractDocuments[0]?.documentCategory) && (
                <Tooltip
                  bg="compliance.tooltip.bg"
                  borderColor="compliance.tooltip.border"
                  color="compliance.tooltip.text"
                  label={`${
                    contractDocuments[0].documentVersion?.document.form ||
                    contractDocuments[0].documentCategory
                  } - ${contractDocuments[0].documentVersion?.document.name}`}
                >
                  <Tag
                    bg="compliance.tag.bg"
                    borderRadius="xs"
                    color="compliance.text.muted"
                    flexShrink={0}
                    fontSize="xs"
                    px={1}
                    py={0}
                    size="sm"
                    variant="subtle"
                    whiteSpace="nowrap"
                  >
                    {contractDocuments[0].documentVersion?.document.form ||
                      contractDocuments[0].documentCategory}
                  </Tag>
                </Tooltip>
              )}

            <Tooltip
              bg="compliance.tooltip.bg"
              borderColor="compliance.tooltip.border"
              color="compliance.tooltip.text"
              label={document.documentName}
            >
              <Text
                color="compliance.text.primary"
                fontSize="sm"
                fontWeight="medium"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {document.documentName}
              </Text>
            </Tooltip>
          </Flex>

          {!(subtitleType === 'status' && reviewToDisplay) && (
            <ValidationStatusIcon
              errorCount={totalErrorCount}
              status={validationStatus}
            />
          )}
        </Flex>

        {/* Subtitle Row */}
        {showSubtitle && subtitleType && (
          <Box ml="18px">
            {subtitleType === 'folder' && (
              <Text color="compliance.text.muted" fontSize="xs">
                {document.folderName}
              </Text>
            )}
            {subtitleType === 'status' &&
              (reviewToDisplay || document.complianceStatus) && (
                <Tag
                  bg="compliance.tag.bg"
                  borderRadius="xs"
                  color="compliance.text.muted"
                  fontSize="xs"
                  px={1}
                  py={0}
                  size="sm"
                >
                  {reviewToDisplay
                    ? reviewToDisplay.status ===
                      IngestionDocumentReviewStatus.APPROVED
                      ? 'Approved'
                      : 'Returned'
                    : formatComplianceStatus(document.complianceStatus || '')}
                </Tag>
              )}
          </Box>
        )}
      </VStack>

      {hasMultipleContractDocs && (
        <Collapse in={!isCollapsed}>
          <VStack align="stretch" mt={1} spacing={2}>
            <Divider my={2} />

            {contractDocuments.map((contractDoc) => {
              const contractDocErrorCount = getContractDocErrorCount(
                contractDoc.id
              );
              // For nested contract document lines, always show validation status (error/success/pending), never review status
              const contractValidationStatus = getValidationStatus(
                isValidated,
                contractDocErrorCount,
                shouldShowStatus,
                null, // Pass null to ignore document review status for contract document lines
                true // Always classified since we have a contract document.
              );

              return (
                <Flex
                  key={contractDoc.id}
                  _hover={{
                    color: 'compliance.text.secondary',
                  }}
                  align="center"
                  color="compliance.text.muted"
                  cursor="pointer"
                  fontSize="sm"
                  gap={1}
                  justifyContent="space-between"
                  onClick={(e) => handleContractDocClick(contractDoc.id, e)}
                >
                  <Flex align="center" gap={1} overflow="hidden">
                    <Icon as={FileTextIcon} h={2.5} w={2.5} />
                    <Tooltip label={getContractDocDisplayName(contractDoc)}>
                      <Text
                        fontSize="sm"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {getContractDocDisplayName(contractDoc)}
                      </Text>
                    </Tooltip>
                  </Flex>

                  <ValidationStatusIcon
                    errorCount={contractDocErrorCount}
                    status={contractValidationStatus}
                  />
                </Flex>
              );
            })}
          </VStack>
        </Collapse>
      )}
    </Box>
  );
};
